import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useReducedMotion } from "motion/react";
import { format, startOfWeek } from "date-fns";
import {
  ChevronLeft, ChevronRight, Plus, X, Check, Send, Save, CopyPlus,
  Sparkles, Plane, PartyPopper, CalendarOff, ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useDateLocale } from "@/hooks/useDateLocale";
import { TimesheetStatusBadge } from "@/components/timekeeping/TimesheetStatusBadge";
import type { TimesheetWeek, TimekeepingTask, TimesheetEntryInput } from "@/lib/api";
import { formatLocalizedDate } from "@/lib/localized-date";

interface Activity { id: string; taskId: number; hours: number; }
interface DayDraft { activities: Activity[]; note: string; }
type DayMap = Record<string, DayDraft>;

const HOUR_PRESETS = [2, 4, 6, 8];
const uid = () => Math.random().toString(36).slice(2, 9);

function initFromWeek(week: TimesheetWeek): DayMap {
  const map: DayMap = {};
  for (const day of week.days) {
    const activities: Activity[] = [];
    let note = "";
    for (const e of day.entries) {
      if (e.task_id != null) activities.push({ id: uid(), taskId: e.task_id, hours: Number(e.hours) });
      if (!note && e.notes && e.notes.trim()) note = e.notes;
    }
    map[day.date] = { activities, note };
  }
  return map;
}

function weekdayNumber(date: string): number {
  const d = new Date(date + "T00:00:00Z").getUTCDay();
  return d;
}

interface Props {
  week: TimesheetWeek;
  tasks: TimekeepingTask[];
  editable: boolean;
  saving: boolean;
  submitting: boolean;
  /** When false (offline), Submit and Copy are disabled — Save still queues. */
  online?: boolean;
  /** Hide the Submit button (e.g. admin entering on behalf of an employee). Default true. */
  showSubmit?: boolean;
  /** Hide the Copy-previous button. Default true. */
  showCopy?: boolean;
  /** Tenant validation settings used for immediate client-side feedback. */
  maxDailyHours?: number;
  allowFutureDates?: boolean;
  onPrevWeek: () => void;
  onNextWeek: () => void;
  onThisWeek: () => void;
  onSaveDraft: (entries: TimesheetEntryInput[]) => void;
  onSubmit?: (entries: TimesheetEntryInput[]) => void;
  onCopyPrevious?: () => void;
}

export function WeekFlow(props: Props) {
  const { week, tasks, editable } = props;
  const online = props.online ?? true;
  const showSubmit = props.showSubmit ?? true;
  const showCopy = props.showCopy ?? true;
  const maxDailyHours = props.maxDailyHours ?? 24;
  const allowFutureDates = props.allowFutureDates ?? false;
  const { t, i18n } = useTranslation();
  const locale = useDateLocale();
  const language = i18n.resolvedLanguage || i18n.language;
  const reduce = useReducedMotion();

  const [dayMap, setDayMap] = useState<DayMap>(() => initFromWeek(week));
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);

  // Re-init only when the underlying week content changes (navigation, save,
  // copy, submit) — never mid-typing, so local edits are preserved.
  const contentKey = `${week.weekStartDate}:${week.status}:${week.timesheetId ?? "new"}:${week.totalHours}`;
  const lastKey = useRef<string>("");
  const lastWeek = useRef<string>("");
  useEffect(() => {
    if (lastKey.current !== contentKey) {
      lastKey.current = contentKey;
      setDayMap(initFromWeek(week));
    }
    if (lastWeek.current !== week.weekStartDate) {
      lastWeek.current = week.weekStartDate;
      const idx = week.days.findIndex((d) => d.date === format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"));
      const todayIdx = week.days.findIndex((d) => d.date === new Date().toISOString().slice(0, 10));
      setStep(todayIdx >= 0 ? todayIdx : idx >= 0 ? idx : 0);
    }
  }, [contentKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dates = week.days.map((d) => d.date);
  const day = week.days[step];
  const dayState = dayMap[day.date] ?? { activities: [], note: "" };
  const today = new Date().toISOString().slice(0, 10);
  const futureBlocked = editable && day.date > today && !allowFutureDates;
  const loggable = editable && !day.leave && !day.holiday && !day.outOfEmployment && !futureBlocked;

  const dayHours = (date: string) => (dayMap[date]?.activities ?? []).reduce((s, a) => s + a.hours, 0);
  const totalHours = useMemo(
    () => dates.reduce((s, d) => s + dayHours(d), 0),
    [dayMap], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const overLimitDates = dates.filter((date) => dayHours(date) > maxDailyHours);
  const hasDailyLimitError = overLimitDates.length > 0;
  const workingWeekdays = week.workingWeekdays ?? [1, 2, 3, 4, 5];
  const effectiveRequiredHours = Number.isFinite(Number(week.effectiveRequiredHours))
    ? Number(week.effectiveRequiredHours)
    : week.requiredHours;
  const scheduledRequiredHours = Number.isFinite(Number(week.scheduledRequiredHours))
    ? Number(week.scheduledRequiredHours)
    : week.requiredHours;

  const patchDay = (date: string, next: Partial<DayDraft>) =>
    setDayMap((m) => ({ ...m, [date]: { ...(m[date] ?? { activities: [], note: "" }), ...next } }));

  const toggleTask = (taskId: number) => {
    const acts = dayState.activities;
    const exists = acts.find((a) => a.taskId === taskId);
    if (exists) patchDay(day.date, { activities: acts.filter((a) => a.taskId !== taskId) });
    else patchDay(day.date, { activities: [...acts, { id: uid(), taskId, hours: acts.length === 0 ? 8 : 0 }] });
  };
  const setActivityHours = (id: string, hours: number) =>
    patchDay(day.date, { activities: dayState.activities.map((a) => (a.id === id ? { ...a, hours } : a)) });
  const removeActivity = (id: string) =>
    patchDay(day.date, { activities: dayState.activities.filter((a) => a.id !== id) });

  const applyToWeek = () => {
    setDayMap((m) => {
      const next = { ...m };
      for (const d of week.days) {
        if (d.date === day.date) continue;
        if (!workingWeekdays.includes(weekdayNumber(d.date)) || d.leave || d.holiday || d.outOfEmployment) continue;
        if (d.date > today && !allowFutureDates) continue;
        next[d.date] = {
          activities: dayState.activities.map((a) => ({ ...a, id: uid() })),
          note: dayState.note,
        };
      }
      return next;
    });
  };

  const buildEntries = (): TimesheetEntryInput[] => {
    const out: TimesheetEntryInput[] = [];
    for (const [date, d] of Object.entries(dayMap)) {
      for (const a of d.activities) {
        if (a.hours > 0) out.push({ entryDate: date, taskId: a.taskId, hours: a.hours, notes: d.note.trim() || null });
      }
    }
    return out;
  };

  const go = (next: number) => { setDir(next > step ? 1 : -1); setStep(Math.max(0, Math.min(6, next))); };
  const taskById = (id: number) => tasks.find((tk) => tk.id === id);

  const dayVariants = {
    enter: (d: number) => ({ x: reduce ? 0 : d > 0 ? 48 : -48, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: reduce ? 0 : d > 0 ? -32 : 32, opacity: 0 }),
  };
  const dayTransition = reduce
    ? { duration: 0.15 }
    : { type: "spring" as const, stiffness: 380, damping: 32, opacity: { duration: 0.18 } };

  const requiredHours = effectiveRequiredHours;
  const targetAdjusted = effectiveRequiredHours !== week.requiredHours
    || scheduledRequiredHours !== week.requiredHours;

  return (
    <div className="space-y-5">
      {/* Week navigator (compact, overflow-safe) */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={props.onPrevWeek} aria-label={t("common.previous", { defaultValue: "Previous" })}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0 text-center text-sm font-medium truncate">
            {formatLocalizedDate(week.weekStartDate, language)} – {formatLocalizedDate(week.weekEndDate, language)}
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={props.onNextWeek} aria-label={t("common.next", { defaultValue: "Next" })}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <TimesheetStatusBadge status={week.status} />
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={props.onThisWeek}>
              {t("timekeeping.week.thisWeek", { defaultValue: "This week" })}
            </Button>
            {editable && showCopy && (
              <Button
                variant="ghost" size="icon" className="h-7 w-7 shrink-0"
                disabled={props.saving || !online}
                onClick={() => props.onCopyPrevious?.()}
                title={t("timekeeping.copy.button", { defaultValue: "Copy previous week" })}
                aria-label={t("timekeeping.copy.button", { defaultValue: "Copy previous week" })}
              >
                <CopyPlus className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-baseline gap-1 shrink-0">
            <motion.span
              key={totalHours}
              initial={reduce ? false : { scale: 0.8, opacity: 0.4 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 500, damping: 24 }}
              className="text-xl font-bold tabular-nums text-foreground"
            >
              {totalHours}
            </motion.span>
            <span className="text-xs text-muted-foreground">/ {requiredHours}h</span>
          </div>
        </div>
        {targetAdjusted && (
          <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {t("timekeeping.week.effectiveTargetExplanation", {
              baseline: week.requiredHours,
              effective: effectiveRequiredHours,
              leaveDays: week.leaveDays ?? 0,
              holidayDays: week.holidayDays ?? 0,
              defaultValue: "Standard baseline: {{baseline}}h · Effective target: {{effective}}h (approved leave: {{leaveDays}}, public holidays: {{holidayDays}})",
            })}
          </div>
        )}
      </div>

      {/* Day stepper */}
      <div className="grid grid-cols-7 gap-1.5">
        {week.days.map((d, i) => {
          const active = i === step;
          const hrs = dayHours(d.date);
          const exceedsLimit = hrs > maxDailyHours;
          const blockedFuture = editable && d.date > today && !allowFutureDates;
          const state = d.outOfEmployment ? "out" : d.leave ? "leave" : d.holiday ? "holiday" : blockedFuture ? "future" : hrs > 0 ? "done" : "empty";
          const dot = {
            out: "bg-muted-foreground/30", leave: "bg-purple-500", holiday: "bg-blue-500",
            future: "bg-muted-foreground/30", done: "bg-emerald-500", empty: "bg-muted-foreground/30",
          }[state];
          return (
            <button
              key={d.date}
              onClick={() => go(i)}
              disabled={d.outOfEmployment}
              aria-label={`${format(new Date(d.date + "T00:00:00"), "EEEE d MMMM", { locale })}, ${hrs}h`}
              className={`relative flex flex-col items-center gap-1 py-2 rounded-xl border text-xs transition-colors ${
                exceedsLimit ? "border-destructive bg-destructive/5" : active ? "border-primary bg-primary/5" : "border-border/60 hover:bg-muted/40"
              } ${d.outOfEmployment ? "opacity-40" : ""}`}
            >
              <span className="uppercase text-[10px] text-muted-foreground">{format(new Date(d.date + "T00:00:00"), "EEE", { locale })}</span>
              <span className={`font-semibold ${active ? "text-primary" : ""}`}>{format(new Date(d.date + "T00:00:00"), "d", { locale })}</span>
              {exceedsLimit ? (
                <span className="text-[10px] font-semibold text-destructive">{hrs}h</span>
              ) : state === "done" ? (
                <motion.span initial={reduce ? false : { scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 600, damping: 20 }}>
                  <Check className="h-3 w-3 text-emerald-500" />
                </motion.span>
              ) : (
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Animated day card */}
      <div className="relative overflow-hidden rounded-2xl border bg-card min-h-[320px]">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={day.date}
            custom={dir}
            variants={dayVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={dayTransition}
            className="p-6 space-y-5"
          >
            {/* Day header */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xl font-bold text-foreground">{format(new Date(day.date + "T00:00:00"), "EEEE", { locale })}</div>
                <div className="text-sm text-muted-foreground">{formatLocalizedDate(day.date, language)}</div>
              </div>
              {dayHours(day.date) > 0 && (
                <span className="text-sm font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">{dayHours(day.date)}h</span>
              )}
            </div>

            {/* Non-loggable states */}
            {day.outOfEmployment ? (
              <FlowState icon={<CalendarOff className="h-8 w-8" />} title={t("timekeeping.flow.outTitle", { defaultValue: "Outside the employment period" })}
                sub={t("timekeeping.flow.outSub", { defaultValue: "No time can be logged on this day." })} />
            ) : day.leave ? (
              <FlowState icon={<Plane className="h-8 w-8" />} title={t(`timekeeping.leaveTypes.${day.leave.type}`, { defaultValue: day.leave.type })}
                sub={t("timekeeping.flow.leaveSub", { defaultValue: "This day is covered by approved leave." })} tone="purple" />
            ) : day.holiday ? (
              <FlowState icon={<PartyPopper className="h-8 w-8" />} title={t("timekeeping.entry.holiday", { defaultValue: "Public holiday" })}
                sub={t("timekeeping.flow.holidaySub", { defaultValue: "Enjoy your day off — nothing to log." })} tone="blue" />
            ) : futureBlocked ? (
              <FlowState icon={<CalendarOff className="h-8 w-8" />} title={t("timekeeping.flow.futureTitle", { defaultValue: "Future entry is disabled" })}
                sub={t("timekeeping.flow.futureSub", { defaultValue: "An administrator can enable future-date entries in the timekeeping settings." })} />
            ) : !editable ? (
              <ReadOnlyDay activities={dayState.activities} note={dayState.note} taskById={taskById} t={t} />
            ) : (
              <>
                {/* Task chips */}
                <div>
                  <div className="text-sm text-muted-foreground mb-2">{t("timekeeping.flow.prompt", { defaultValue: "What did you work on?" })}</div>
                  <div className="flex flex-wrap gap-2">
                    {tasks.map((tk) => {
                      const selected = dayState.activities.some((a) => a.taskId === tk.id);
                      return (
                        <motion.button
                          key={tk.id}
                          whileTap={reduce ? undefined : { scale: 0.94 }}
                          onClick={() => toggleTask(tk.id)}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                            selected ? "border-transparent text-white" : "bg-background hover:bg-muted/50"
                          }`}
                          style={selected ? { background: tk.color ?? "#6366f1" } : undefined}
                        >
                          {!selected && <span className="h-2.5 w-2.5 rounded-full" style={{ background: tk.color ?? "#94a3b8" }} />}
                          {tk.name}
                          {selected && <Check className="h-3.5 w-3.5" />}
                        </motion.button>
                      );
                    })}
                    {tasks.length === 0 && (
                      <span className="text-sm text-muted-foreground">{t("timekeeping.week.noTasksDefined", { defaultValue: "No tasks are defined yet. Ask HR to add tasks." })}</span>
                    )}
                  </div>
                </div>

                {/* Selected activities with hours */}
                <AnimatePresence initial={false}>
                  {dayState.activities.map((a) => {
                    const tk = taskById(a.taskId);
                    return (
                      <motion.div
                        key={a.id} layout
                        initial={reduce ? false : { opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduce ? { opacity: 0 } : { opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        className="flex flex-wrap items-center gap-2 rounded-xl border bg-background/60 px-3 py-2.5"
                      >
                        <span className="inline-flex items-center gap-2 min-w-0 w-full sm:flex-1 sm:w-auto">
                          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: tk?.color ?? "#94a3b8" }} />
                          <span className="truncate text-sm font-medium">{tk?.name ?? `#${a.taskId}`}</span>
                        </span>
                        <div className="flex items-center gap-1 ml-auto">
                          {HOUR_PRESETS.map((h) => (
                            <button
                              key={h}
                              onClick={() => setActivityHours(a.id, h)}
                              className={`h-8 w-9 rounded-md text-xs font-medium tabular-nums transition-colors ${
                                a.hours === h ? "bg-primary text-primary-foreground" : "bg-muted/60 hover:bg-muted"
                              }`}
                            >
                              {h}h
                            </button>
                          ))}
                          <Input
                            type="number" min={0} max={maxDailyHours} step={0.25}
                            value={a.hours || ""}
                            onChange={(e) => setActivityHours(a.id, Math.max(0, Math.min(24, Number(e.target.value))))}
                            className="h-8 w-16 text-center tabular-nums"
                          />
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeActivity(a.id)}>
                            <X className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>

                {dayHours(day.date) > maxDailyHours && (
                  <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                    {t("timekeeping.errors.dailyLimit", {
                      limit: maxDailyHours,
                      defaultValue: "You cannot enter more than {{limit}} working hours in one day.",
                    })}
                  </div>
                )}

                {dayState.activities.length === 0 && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Plus className="h-4 w-4" />{t("timekeeping.flow.pickTask", { defaultValue: "Pick a task above to log your hours." })}
                  </div>
                )}

                {/* Day comment */}
                <div>
                  <label className="text-sm text-muted-foreground">{t("timekeeping.flow.commentLabel", { defaultValue: "Notes about your day (optional)" })}</label>
                  <Textarea
                    value={dayState.note}
                    onChange={(e) => patchDay(day.date, { note: e.target.value })}
                    placeholder={t("timekeeping.flow.commentPlaceholder", { defaultValue: "For example: regular shift, receiving goods, or helping customers." })}
                    className="mt-1 min-h-[64px]"
                  />
                </div>

                {/* Apply to whole week */}
                {dayState.activities.length > 0 && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={applyToWeek}>
                    <Sparkles className="h-4 w-4" />{t("timekeeping.flow.applyWeek", { defaultValue: "Use this for the whole week" })}
                  </Button>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom nav + actions */}
      {hasDailyLimitError && (
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {t("timekeeping.errors.dailyLimit", {
            limit: maxDailyHours,
            defaultValue: "You cannot enter more than {{limit}} working hours in one day.",
          })}
        </div>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* Day nav */}
        <div className="flex items-center justify-between sm:justify-start gap-2">
          <Button variant="ghost" size="sm" onClick={() => go(step - 1)} disabled={step === 0} className="gap-1">
            <ChevronLeft className="h-4 w-4" />{t("common.back", { defaultValue: "Back" })}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => go(step + 1)} disabled={step === 6} className="gap-1">
            {t("common.next", { defaultValue: "Next" })}<ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Save / Submit */}
        {editable && (
          <div className={`grid ${showSubmit ? "grid-cols-2" : "grid-cols-1"} gap-2 sm:flex sm:items-center`}>
            <Button variant="outline" className="w-full sm:w-auto gap-1.5" disabled={props.saving || hasDailyLimitError} onClick={() => props.onSaveDraft(buildEntries())}>
              <Save className="h-4 w-4" />{t("timekeeping.week.saveDraft", { defaultValue: "Save draft" })}
            </Button>
            {showSubmit && (
              <Button className="w-full sm:w-auto gap-1.5 gradient-brand text-primary-foreground" disabled={props.submitting || !online || hasDailyLimitError} onClick={() => props.onSubmit?.(buildEntries())}>
                <Send className="h-4 w-4" />{t("timekeeping.week.submit", { defaultValue: "Submit week" })}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FlowState({ icon, title, sub, tone }: { icon: React.ReactNode; title: string; sub: string; tone?: "purple" | "blue" }) {
  const color = tone === "purple" ? "text-purple-500" : tone === "blue" ? "text-blue-500" : "text-muted-foreground";
  return (
    <div className="flex flex-col items-center justify-center text-center py-10 gap-2">
      <div className={color}>{icon}</div>
      <div className="text-base font-semibold text-foreground">{title}</div>
      <div className="text-sm text-muted-foreground max-w-sm">{sub}</div>
    </div>
  );
}

function ReadOnlyDay({ activities, note, taskById, t }: {
  activities: Activity[]; note: string; taskById: (id: number) => TimekeepingTask | undefined; t: (k: string, o?: any) => string;
}) {
  if (activities.length === 0 && !note) {
    return <div className="text-sm text-muted-foreground py-6 text-center">{t("timekeeping.week.noneLogged", { defaultValue: "No time logged." })}</div>;
  }
  return (
    <div className="space-y-2">
      {activities.map((a) => {
        const tk = taskById(a.taskId);
        return (
          <div key={a.id} className="flex items-center justify-between rounded-xl border bg-background/60 px-3 py-2">
            <span className="inline-flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: tk?.color ?? "#94a3b8" }} />
              <span className="text-sm">{tk?.name ?? `#${a.taskId}`}</span>
            </span>
            <span className="text-sm font-semibold tabular-nums">{a.hours}h</span>
          </div>
        );
      })}
      {note && <div className="text-sm text-muted-foreground italic px-1">“{note}”</div>}
    </div>
  );
}
